import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GoogleMap, useLoadScript, Marker, Autocomplete } from "@react-google-maps/api";
import { showSuccess, showError } from "toast/toast";
import { useNavigate, useParams, Link } from "react-router-dom";
import Select, { SingleValue } from "react-select";
import { License, Office, Agent, Address, Product, PaymentMethod, PaymentGateway, StatusHistoryItem, PaymentInfoItem, Supplier } from "types";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import PaymentFormManualOrder from "../../components/PaymentFormManualOrder";
import { StatusBadge } from "components/Productlocationstatus";
import { getAuthCookie } from "../../utils/authCookies";
import {
  postInstallationDynamicCost,
  resolveInstallationSellerOrganizationId,
  resolveInstallationVendorSelectOption,
  resolveInstallationInstallerSelectOption,
  mergeInstallationOrderBillingAddress,
  resolveInstallationAddressMembershipId,
  resolveInstallationPaymentMethodsMembershipId,
  fetchInstallationPaymentMethods,
  installationOrderUrls,
  fetchUserAddresses,
} from "api/installationApi";
type ProductObj = {
  product_id: string;
  agent_id: string;
  office_id: string;
  inventory_type: string;
  product: Product;
  linked_installer_details: any
  created_by: {
    id: string;
    name: string;
  };
};

type Selected = {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

const libraries: Array<"places"> = ["places"];
const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const mapContainerStyle = {
  width: "100%",
  height: "350px",
  borderRadius: "12px",
  overflow: "hidden",
  // boxShadow: "0 0 10px rgba(0,0,0,0.1)", // Soft shadow
  // border: "1px solid #dee2e6",          // Light border
};
const center = {
  lat: 40.712776, // Default latitude (e.g., New York City)
  lng: -74.005974, // Default longitude
};

const AgentEditInstallation = () => {
  const { installationId } = useParams<{ installationId: string }>();
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const userName = getAuthCookie("userName");
  const [preview, setPreview] = useState<string | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [quantity, setQuantity] = useState(1);
  const [addressObjects, setAddressObjects] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [productObjs, setProductObjs] = useState<ProductObj[]>([]);
  const [selectedProductOption, setSelectedProductOption] = useState<any | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [installationCost, setInstallationCost] = useState<number | null>(null);
  const [totalUnits, setTotalUnits] = useState<number>(0);
  const [uploadedFileMeta, setUploadedFileMeta] = useState<any[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>([]);
  const [isMarkerDraggable, setIsMarkerDraggable] = useState(false);
  const [installationOrder, setInstallationOrder] = useState<any>();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod[]>([]);
  const [payment, setPayment] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>("");

  const navigate = useNavigate();

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const [selectedAgent, setSelectedAgent] = useState<Selected | null>(null);

  const [selectedBillingAddress, setSelectedBillingAddress] = useState<Address | null>(null);

  const [installationAddress, setInstallationAddress] = useState<Address>();

  const [selectedInstaller, setSelectedInstaller] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const installerOptions = useMemo(() => {
    const fromSuppliers =
      suppliers?.map((supplier) => ({
        value: supplier.supplier_id,
        label: supplier.name,
      })) ?? [];
    const vendor = resolveInstallationVendorSelectOption(installationOrder);
    if (vendor && !fromSuppliers.some((opt) => opt.value === vendor.value)) {
      return [...fromSuppliers, vendor];
    }
    return fromSuppliers;
  }, [suppliers, installationOrder]);

  const productOptions = productObjs?.map((item) => ({
    value: item.product_id,
    label: item.product.name,
  }));

  useEffect(() => {
    // console.log("Installation ID:", installationId);
    const fetchOrderDetails = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/installation_orders/${installationId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json();
        console.log("Installation Orders:", data);
        if (!response.ok) throw new Error(data.message || "Failed to fetch order details");

        setInstallationOrder(data);
      } catch (err: any) {
        console.error("Order Detail Fetch Error:", err.message);
      }
    };

    fetchOrderDetails();
  }, [installationId, token]);

  useEffect(() => {
    const fetchSuppliersByUserId = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/agent/suppliers/${userId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch supplier list");
        }

        const data = await response.json();
        console.log("Suppliers:", data);
        setSuppliers(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSuppliersByUserId();
  }, [token, userId])

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  useEffect(() => {
    const fetchAddresses = async () => {
      const routeMembershipId =
        resolveInstallationAddressMembershipId(installationOrder) || userId || "";
      if (!routeMembershipId || !token) return;
      try {
        const data = await fetchUserAddresses(token, routeMembershipId);
        const rows = Array.isArray(data) ? data : [];
        setAddressObjects(
          mergeInstallationOrderBillingAddress(rows, installationOrder?.billing_address)
        );
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    if (installationOrder || userId) {
      void fetchAddresses();
    }
  }, [token, installationOrder, userId]);

  const addressOptions =
    addressObjects !== null
      ? addressObjects?.map((address) => ({
        ...address,
        value: address._id,
        label: `${address.label}: ${address.address_details?.addressLine1}, ${address.address_details?.city}, ${address.address_details?.state}, ${address.address_details?.zip ? address.address_details.zip : ""}`,
      }))
      : [];

  const fetchLinkedProducts = async (
    signageCompanyId: string
  ) => {
    console.log("fetchLinkedProducts IN");
    if (!userId || !signageCompanyId) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/inventory/agent/${userId}/signage/${signageCompanyId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      console.log("Linked Products:", data);
      setProductObjs(data);

      if (!response.ok) {
        showError(data?.error || "Failed to fetch linked products");
      } else {
        // setLinkedProducts(data); if needed
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred");
    }
  };

  // based on instaletion Address we are calling the dyniamc cost
  useEffect(() => {
    const fetchInstallationCost = async () => {
      if (
        !installationAddress?.latitude ||
        !installationAddress?.longitude ||
        installationAddress.latitude === 0 ||
        installationAddress.longitude === 0
      ) {
        showError(
          "Selected installation address is missing latitude/longitude. Please enter the address manually."
        );
        return;
      }

      try {
        const sellerOrgId = resolveInstallationSellerOrganizationId(
          selectedInstaller?.value,
          installationOrder
        );
        const data = await postInstallationDynamicCost(token, sellerOrgId, {
          installation_address: {
            addressLine1: installationAddress.addressLine1,
            addressLine2: installationAddress.addressLine2,
            city: installationAddress.city,
            state: installationAddress.state,
            country: installationAddress.country,
            zip: installationAddress.zip,
            cityCode: installationAddress.cityCode,
            stateCode: installationAddress.stateCode,
            countryCode: installationAddress.countryCode,
            latitude: installationAddress.latitude,
            longitude: installationAddress.longitude,
          },
        });
        console.log("Installation cost:", data);
        setInstallationCost(data.cost || 0);
      } catch (error: any) {
        console.error("Cost fetch error:", error.message);
        setInstallationCost(null);
      }
    };
    if (installationAddress) {
      fetchInstallationCost();
    }
  }, [installationAddress, selectedInstaller]);

  // ===== Types =====
  type Status = "on_site" | "with_supplier";

  type LinkedInstallerLocation = {
    quantity: number;
    warehouse_id: string;
    warehouse_name?: string;
    zone_id?: string;
    zone_name?: string;
    rack_id?: string;
    rack_name?: string;
    bin_id?: string;
    bin_name?: string;
  };

  type LinkedInstallerRow = {
    current_status: Status;                 // "on_site" | "with_supplier"
    product_location_status: string;        // mirror to result rows
    quantity?: number;                      // for bucket rows
    location?: LinkedInstallerLocation[];   // for per-location rows
  };

  type LinkedQuantityLocation = {
    quantity: number;
    current_status: Status;                 // EXACTLY "on_site" or "with_supplier"
    product_location_status: string;
    location?: Array<LinkedInstallerLocation & { quantity: number }>;
  };

  // ===== Normalizers & helpers =====
  function normalizeStatus(s: any): Status {
    const k = String(s || "").trim().toLowerCase();
    if (k === "on_site" || k === "on site" || k === "onsite") return "on_site";
    if (k === "with_supplier" || k === "with supplier" || k === "withsupplier") return "with_supplier";
    return "with_supplier";
  }

  function getLiRowsFromMatched(matched: any): LinkedInstallerRow[] {
    const rows: any[] = matched?.linked_installer_details?.storage_locations ?? [];
    return rows.map((r) => {
      const status = normalizeStatus(r.current_status);
      const hasLocs = Array.isArray(r.location) && r.location.length > 0;
      return {
        current_status: status,
        product_location_status: String(r.product_location_status || ""),
        quantity: hasLocs ? undefined : Number(r.quantity) || 0,
        location: hasLocs
          ? r.location.map((loc: any) => ({
            quantity: Number(loc.quantity) || 0,
            warehouse_id: String(loc.warehouse_id || ""),
            warehouse_name: String(loc.warehouse_name || ""),
            zone_id: String(loc.zone_id || ""),
            zone_name: String(loc.zone_name || ""),
            rack_id: String(loc.rack_id || ""),
            rack_name: String(loc.rack_name || ""),
            bin_id: String(loc.bin_id || ""),
            bin_name: String(loc.bin_name || ""),
          }))
          : [],
      };
    });
  }

  const sumAll = (rows: LinkedInstallerRow[]) =>
    rows.reduce((t, r) => {
      if (r.location?.length) {
        return t + r.location.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
      }
      return t + (Number(r.quantity) || 0);
    }, 0);

  const sumStorage = (items: LinkedQuantityLocation[] = []) =>
    items.reduce((t, r) => t + (Number(r.quantity) || 0), 0);

  // ===== STRICT allocator =====
  // - on_site first
  // - with_supplier bucket rows wrapped into location[0] with blank IDs
  // - with_supplier per-location combined into ONE entry per row (location[] holds all splits)
  function allocateFromLinkedInstallerStrict(
    requestedQty: number,
    liRows: LinkedInstallerRow[],
  ): LinkedQuantityLocation[] {
    if (!Number.isFinite(requestedQty) || requestedQty <= 0) {
      throw new Error(`Invalid requestedQty: ${requestedQty}`);
    }
    if (!Array.isArray(liRows)) throw new Error("linked_installer rows invalid");

    const liTotal = sumAll(liRows);
    if (liTotal < requestedQty) {
      throw new Error(
        `Insufficient linked_installer stock (${liTotal}) for requested quantity (${requestedQty})`
      );
    }

    let remaining = requestedQty;
    const result: LinkedQuantityLocation[] = [];

    // A) ON_SITE buckets
    const rowsOnSiteBuckets = liRows.filter(
      (r) => r.current_status === "on_site" && !(r.location?.length)
    );
    for (const row of rowsOnSiteBuckets) {
      if (remaining === 0) break;
      const take = Math.min(remaining, Number(row.quantity) || 0);
      if (take > 0) {
        result.push({
          quantity: take,
          current_status: "on_site",
          product_location_status: row.product_location_status,
        });
        remaining -= take;
      }
    }

    // B1) WITH_SUPPLIER bucket rows (wrap into single location item)
    const rowsSupplierBuckets = liRows.filter(
      (r) => r.current_status === "with_supplier" && !(r.location?.length)
    );
    for (const row of rowsSupplierBuckets) {
      if (remaining === 0) break;
      const take = Math.min(remaining, Number(row.quantity) || 0);
      if (take > 0) {
        result.push({
          quantity: take,
          current_status: "with_supplier",
          product_location_status: row.product_location_status,
          location: [
            {
              quantity: take,
              warehouse_id: "",
              warehouse_name: "",
              zone_id: "",
              zone_name: "",
              rack_id: "",
              rack_name: "",
              bin_id: "",
              bin_name: "",
            },
          ],
        });
        remaining -= take;
      }
    }

    // B2) WITH_SUPPLIER per-location rows (combine into one entry per row)
    const rowsSupplierPerLoc = liRows.filter(
      (r) => r.current_status === "with_supplier" && (r.location?.length ?? 0) > 0
    );
    for (const row of rowsSupplierPerLoc) {
      if (remaining === 0) break;

      const locArray: LinkedInstallerLocation[] = [];
      for (const loc of row.location || []) {
        if (remaining === 0) break;
        const take = Math.min(remaining, Number(loc.quantity) || 0);
        if (take > 0) {
          locArray.push({
            quantity: take,
            warehouse_id: loc.warehouse_id,
            warehouse_name: loc.warehouse_name,
            zone_id: loc.zone_id,
            zone_name: loc.zone_name,
            rack_id: loc.rack_id,
            rack_name: loc.rack_name,
            bin_id: loc.bin_id,
            bin_name: loc.bin_name,
          });
          remaining -= take;
        }
      }

      if (locArray.length > 0) {
        result.push({
          quantity: locArray.reduce((sum, l) => sum + l.quantity, 0),
          current_status: "with_supplier",
          product_location_status: row.product_location_status,
          location: locArray,
        });
      }
    }

    if (remaining > 0) throw new Error("insufficient stock after allocation");

    const sum = result.reduce((s, r) => s + r.quantity, 0);
    if (sum !== requestedQty) throw new Error(`Allocation sum (${sum}) != requested quantity (${requestedQty})`);

    // enforce on_site-first invariant if any on_site supply exists
    const onSiteAvail = rowsOnSiteBuckets.reduce((n, r) => n + (Number(r.quantity) || 0), 0);
    if (onSiteAvail > 0) {
      const expectedOnSite = Math.min(onSiteAvail, requestedQty);
      const allocatedOnSite = result.filter(r => r.current_status === "on_site")
        .reduce((n, r) => n + r.quantity, 0);
      if (allocatedOnSite < expectedOnSite) {
        throw new Error(`On-site-first violated; expected ${expectedOnSite}, got ${allocatedOnSite}`);
      }
    }

    return result;
  }

  // ===== Merge & reconcile for EDIT =====
  function mergeSupplierEntries(items: LinkedQuantityLocation[]): LinkedQuantityLocation[] {
    const onSite = items.filter(i => i.current_status === "on_site");
    const supplier = items.filter(i => i.current_status === "with_supplier");
    if (supplier.length <= 1) return items;

    // If product_location_status differs, pick the first (or add your policy)
    const pls = supplier[0]?.product_location_status || "";
    const combinedLoc = supplier.flatMap(s => s.location ?? []);
    const merged: LinkedQuantityLocation = {
      quantity: combinedLoc.reduce((n, l) => n + (Number(l.quantity) || 0), 0),
      current_status: "with_supplier",
      product_location_status: pls,
      location: combinedLoc,
    };
    return [...onSite, merged];
  }

  function trimStorageDown(oldStorage: LinkedQuantityLocation[], toRemove: number): LinkedQuantityLocation[] {
    let remaining = toRemove;
    const copy = JSON.parse(JSON.stringify(oldStorage)) as LinkedQuantityLocation[];

    // Trim supplier per-location first
    const sIdx = copy.findIndex(s => s.current_status === "with_supplier");
    if (sIdx >= 0 && remaining > 0) {
      const s = copy[sIdx];
      if (Array.isArray(s.location) && s.location.length > 0) {
        for (let i = s.location.length - 1; i >= 0 && remaining > 0; i--) {
          const loc = s.location[i];
          const take = Math.min(remaining, Number(loc.quantity) || 0);
          loc.quantity -= take;
          remaining -= take;
          if (loc.quantity <= 0) s.location.splice(i, 1);
        }
        s.quantity = (s.location ?? []).reduce((n, l) => n + (Number(l.quantity) || 0), 0);
        if ((s.location?.length ?? 0) === 0) copy.splice(sIdx, 1);
      }
    }

    // Trim on_site last
    if (remaining > 0) {
      const onSite = copy.find(s => s.current_status === "on_site");
      if (onSite) {
        const take = Math.min(remaining, Number(onSite.quantity) || 0);
        onSite.quantity -= take;
        remaining -= take;
        if (onSite.quantity <= 0) {
          const idx = copy.indexOf(onSite);
          copy.splice(idx, 1);
        }
      }
    }

    if (remaining > 0) throw new Error(`Cannot trim more than allocated; leftover ${remaining}`);
    return copy;
  }

  function reconcileStorageForEdit(
    originalStorage: LinkedQuantityLocation[] | undefined,
    requestedQty: number,
    liRows: LinkedInstallerRow[]
  ): LinkedQuantityLocation[] {
    const old = originalStorage ?? [];
    const oldQty = sumStorage(old);

    if (requestedQty === oldQty) {
      return old; // keep exact same breakdown
    }

    if (requestedQty > oldQty) {
      const delta = requestedQty - oldQty;
      const extra = allocateFromLinkedInstallerStrict(delta, liRows);
      return mergeSupplierEntries([...old, ...extra]);
    }

    // requested < old
    const trimmed = trimStorageDown(old, oldQty - requestedQty);
    const sum = sumStorage(trimmed);
    if (sum !== requestedQty) throw new Error(`After trim, sum ${sum} != requested ${requestedQty}`);
    return mergeSupplierEntries(trimmed);
  }


  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      userType: installationOrder?.user_type,
      userFirstName: "",
      userLastName: "",
      userEmail: installationOrder?.user_email,
      userPhone: installationOrder?.user_phone,
      brokerageFirmsId: installationOrder?.brokerage_firm_id,
      teamMemberId: installationOrder?.team_member_id,
      company: "",
      installer: resolveInstallationSellerOrganizationId(
        installationOrder?.signage_id,
        installationOrder
      ),
      office: installationOrder?.office,
      agent: installationOrder?.agent_id,
      scheduled_date: installationOrder?.installation_details?.scheduled_date?.split("T")[0] || "",
      jobs_today: 0,
      installeId: installationOrder?.installation_details.installer_id,
      priority: installationOrder?.priority,
      billing_address: installationOrder?.billing_address,
      installation_address: installationOrder?.installation_details.installation_address,
      isSameAsBilling: !!installationOrder?.both_address_same,
      billingAddressId: installationOrder?.billing_address?._id,
      installationAddressId: installationOrder?.installation_details.installation_address.label || "",
      location: "",
      products: "",
      instructions: "",
      payment_method: "",
      image: null,
      installation_charge: installationOrder?.installation_cost,
      discount: installationOrder?.discount_amount || 0,
      subtotal: installationOrder?.subtotal_amount,
      total: installationOrder?.total_amount,
      tax: 0,
      localLawsChecked: false,
      enable_partial_payment: false,
      partial_amount: 0,
      transactionId: "",
      paymentMethod: "",
    },
    validationSchema: Yup.object({
      installer: Yup.string().required("Installer is required"),
      scheduled_date: Yup.string().required("scheduled_date date is required"),
      billingAddressId: Yup.string().required("Billing address is required"),

      installationAddressId: Yup.string().when("isSameAsBilling", {
        is: false,   // ✅ explicitly check false
        then: (schema) => schema.required("Installation address is required"),
        otherwise: (schema) => schema.notRequired()
      }),
      isSameAsBilling: Yup.boolean(),
    }),
    // validationSchema: Yup.object({
    // installer: Yup.string().required("Installer is required"),
    // installation_date: Yup.string().required("Installation date is required"),
    // billing_address: Yup.string().required("Billing address is required"),
    // installation_address: Yup.string().required(
    //     "Installation address is required"
    // ),
    // image: Yup.mixed().required("Image is required"),
    // }),
    onSubmit: async (values) => {
      setFormSubmitting(true);
      const imageUrl = values.image ? await uploadFile(values.image) : null;
      let lineItems: any[] = [];

      // 📦 1. Regular products (agent or office)
      lineItems = selectedProducts.map((product) => {
        const matched = productObjs.find((p) => p.product_id === product.product_id);
        const variant: any = matched?.product?.variants?.[0] || {};
        // 🧠 Check if quantity changed from original order
        const originalItem = installationOrder?.line_items?.find(
          (item: any) => item.product_id === product.product_id
        );
        const originalQty = Number(originalItem?.quantity || 0);
        const currentQty = Number(product.quantity || 0);
        const quantityUnchanged = originalQty === currentQty

        // Build storage_locations only for agent/office
        const invType = (product?.inventory_type || matched?.inventory_type || "").toLowerCase();
        let storage_locations: LinkedQuantityLocation[] = [];

        if (invType === "agent" || invType === "office") {
          const liRows = getLiRowsFromMatched(matched);
          storage_locations = reconcileStorageForEdit(
            originalItem?.storage_locations,
            currentQty,
            liRows
          );
        } else {
          storage_locations = [];
        }

        return {
          product_id: product.product_id,
          variant_id: product.variant_id || variant?.variant_id,
          inventory_source: product?.inventory_type || "", // agent or office
          warehouse_id: null,
          serial_ids: [],
          product_name: product?.name || "",
          sku: product?.sku || "",
          quantity: Number(product.quantity),
          unit_price: Number(product?.retail_price || 0),
          total_price: Number(product?.retail_price || 0) * Number(product.quantity),
          discount: 0,
          tax_amount: 0,
          inventory_deducted: false,
          fulfillment_type: "stock",
          production_status: product.productionStatus || "not started",
          is_rotational: product?.is_rotational || false,
          current_status: product?.product_location_status || "",
          notes: product.notes || "",
          // storage_locations:
          //   product.storage_locations && product.storage_locations.length > 0
          //     ? product.storage_locations.map((loc: any) => ({
          //       ...loc,
          //       quantity: quantityUnchanged ? loc.quantity : Number(product.quantity)
          //     }))
          //     : [
          //       {
          //         quantity: Number(product.quantity),
          //         product_location_status: matched?.linked_installer_details?.storage_locations?.[0]?.product_location_status || "",
          //         current_status: matched?.linked_installer_details?.storage_locations?.[0]?.product_location_status || "",
          //         assigned_at: new Date().toISOString(),
          //         location: matched?.linked_installer_details?.storage_locations?.[0]?.location || {},
          //       }
          //     ],
          storage_locations,
        };
      });

      // 🔄 2. Rotational allocations
      // Object.entries(allocatedUnits).forEach(([linkedProductId, units]) => {
      //     if (units.length === 0) return;
      //     lineItems.push({
      //         product_id: units[0].rotationalProductId,
      //         inventory_source: "rotational",
      //         warehouse_id: units[0].warehouseId || "",
      //         serial_ids: units.map((u) => u.serial_id),
      //         product_name: "",
      //         sku: "",
      //         quantity: 1,
      //         unit_price: 0,
      //         total_price: 0,
      //         discount: 0,
      //         tax_amount: 0,
      //         inventory_deducted: false,
      //         fulfillment_type: "stock",
      //         production_status: null,
      //         is_rotational: true,
      //         current_status: "",
      //         notes: "",
      //     });
      // });
      try {
        // Example file upload handling

        const response = await fetch(`${process.env.REACT_APP_API_URL}/installation_orders/update/${installationId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            // order_number: installationOrder?.order_number,
            // inventory_source: inventorySource,
            // source_id: sourceId,
            // brokerage_firm_id: selectedBrokerageFirm?.value,
            // brokerage_firm_name: selectedBrokerageFirm?.label,
            // team_member_id: values.teamMemberId,
            // office: officeInfo,
            // agent_id: agentId,
            // agent_name: agentName,
            // signage_id: userId,
            // signage_company_name: userName,
            line_items: lineItems,
            // customer_group_id: null,
            // payment_method: values.payment_method,
            installation_details: {
              scheduled_date: new Date(values.scheduled_date).toISOString(),
              installation_address: installationOrder?.installation_details?.installation_address,
              installer_id: selectedInstaller?.value || null,
              installer_name: null,
              contact_number: null,
              estimated_duration: null,
              actual_installation_date: null,
              installation_notes: null,
              assignee_id: null,
              tax_details: null,
            },
            billing_address: installationOrder?.billing_address,
            both_address_same: values.isSameAsBilling,
            additional_instructions: values.instructions,
            order_type: "installation",
            // local_laws_compliance: true,
            priority: values.priority,
            uploaded_files: uploadedFileMeta || [],
            subtotal_amount: values.subtotal,
            installation_cost: parseFloat(String(installationCost)) || 0,
            additional_cost: 0,
            // tax_amount: 0,
            discount_amount: null,
            total_amount: values.total,
            // overpaid_amount: 0,
            version: installationOrder?.version,
            // tax_inclusive: false,
            // tax_details: null,
          }),
        }
        );

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || "Failed to create installation");
        }

        showSuccess("Installation added successfully!");
        navigate("/agent/jobs/installations");
      } catch (error: any) {
        console.error(error);
        showError(`Error: ${error.message}`);
      } finally {
        setFormSubmitting(false);
        setUploadProgress(0);
      }
    },
  });

  const uploadFile = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("uploadDir", "installation_orders");

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          setUploadProgress((event.loaded / event.total) * 100);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200 || xhr.status === 201) {
          const response = JSON.parse(xhr.responseText);
          resolve(response.path);
        } else {
          reject(new Error("Failed to upload file"));
        }
      };

      xhr.onerror = () => reject(new Error("Network error during file upload"));
      xhr.send(formData);
    });
  };

  const handleLocationChange = (
    selectedOption: SingleValue<{ value: string; label: string }>
  ) => {
    formik.setFieldValue("location", selectedOption?.value || "");
  };

  const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      setSelectedLocation({ lat, lng });
      formik.setFieldValue("location", { lat, lng });
    }
  }, []);

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      console.log("Place changed:", place);

      const addressComponents = place.address_components;
      console.log(addressComponents);

      if (addressComponents) {
        const getComponent = (type: string) => {
          const component = addressComponents?.find(c => c.types.includes(type));
          return component ? { long_name: component.long_name, short_name: component.short_name } : { long_name: '', short_name: '' };
        };

        const streetNumber = getComponent('street_number').long_name;
        const route = getComponent('route').long_name;

        const cityObj = getComponent('locality') || getComponent('administrative_area_level_2');
        const stateObj = getComponent('administrative_area_level_1');
        const postalCode = getComponent('postal_code').long_name;
        const countryObj = getComponent('country');


        const street = `${streetNumber} ${route}`.trim();

        const latitude = place.geometry?.location?.lat();
        const longitude = place.geometry?.location?.lng();

        console.log("Latitude:", latitude, "Longitude:", longitude);

        // Update form values
        // formik.setFieldValue('streetAddress1', street);
        // formik.setFieldValue('city', cityObj.long_name);
        // formik.setFieldValue('county', cityObj.long_name);
        // formik.setFieldValue('cityCode', cityObj.short_name); // <-- Add this
        // formik.setFieldValue('state', stateObj.long_name);
        // formik.setFieldValue('stateCode', stateObj.short_name); // <-- Add this
        // formik.setFieldValue('zipcode', postalCode);
        // formik.setFieldValue('country', countryObj.long_name);
        // formik.setFieldValue('countryCode', countryObj.short_name); 

        // if (latitude && longitude) {
        //   formik.setFieldValue('latitude', latitude);
        //   formik.setFieldValue('longitude', longitude);
        // }

        const installation_address = street + ',' + cityObj.long_name + ', ' + stateObj.long_name + ', ' + postalCode;
        setInstallationAddress({
          _id: '',
          label: installation_address,
          addressLine1: street,
          addressLine2: '',
          country: countryObj.long_name,
          county: cityObj.long_name,
          city: cityObj.long_name,
          state: stateObj.long_name,
          zip: postalCode,
          cityCode: cityObj.short_name,
          stateCode: stateObj.short_name,
          countryCode: countryObj.short_name,
          latitude: latitude || 0,
          longitude: longitude || 0,
          phone: '',
        });

        formik.setFieldValue('installationAddressId', installation_address);
      }
    }
  };

  // seting the total units based on the selected products
  useEffect(() => {
    const newTotal = selectedProducts.reduce((sum, product) => sum + (product.quantity || 0), 0);
    setTotalUnits(newTotal);
    console.log("total units:", newTotal);
  }, [selectedProducts]);

  // seting the installation address based on the selected billing address
  useEffect(() => {
    if (formik.values.isSameAsBilling) {
      const billing = selectedBillingAddress;

      if (billing) {
        console.log("Billing Address:", billing);
        setInstallationAddress(billing);
        formik.setFieldValue("installationAddressId", billing.label || "");
      }
    }
  }, [formik.values.isSameAsBilling, selectedBillingAddress]);

  // set default suppliers to get the dynamic cost
  useEffect(() => {
    if (!installationOrder) return;
    const option = resolveInstallationInstallerSelectOption(installationOrder, suppliers);
    if (!option) return;
    setSelectedInstaller(option);
    formik.setFieldValue("installer", option.value);
  }, [installationOrder, suppliers]);

  // calculating the subtotal and total
  useEffect(() => {
    const subtotal = Number(installationCost) + Number(formik.values.tax);
    const total = subtotal - Number(formik.values.discount || 0);

    formik.setFieldValue("subtotal", subtotal);
    formik.setFieldValue("total", total);
  }, [installationCost, formik.values.tax, formik.values.discount]);

  // remove the preview images
  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [imagePreviews]);

  useEffect(() => {
    if (
      installationAddress?.latitude &&
      installationAddress?.longitude &&
      installationAddress.latitude !== 0 &&
      installationAddress.longitude !== 0
    ) {
      console.log("Setting marker from installationAddress");
      setSelectedLocation({
        lat: installationAddress.latitude,
        lng: installationAddress.longitude,
      });
    }
  }, [installationAddress]);

  const handleMarkerDragEnd = async (event: google.maps.MapMouseEvent) => {
    const lat = event.latLng?.lat();
    const lng = event.latLng?.lng();

    if (!lat || !lng) return;

    setSelectedLocation({ lat, lng });

    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ location: { lat, lng } });

      if (response.results[0]) {
        const place = response.results[0];
        const addressComponents = place.address_components;

        const getComponent = (type: string) => {
          const component = addressComponents.find((c: any) =>
            c.types.includes(type)
          );
          return component
            ? { long_name: component.long_name, short_name: component.short_name }
            : { long_name: "", short_name: "" };
        };

        const streetNumber = getComponent("street_number").long_name;
        const route = getComponent("route").long_name;
        const cityObj =
          getComponent("locality") ||
          getComponent("administrative_area_level_2");
        const stateObj = getComponent("administrative_area_level_1");
        const postalCode = getComponent("postal_code").long_name;
        const countryObj = getComponent("country");

        const street = `${streetNumber} ${route}`.trim();

        const newAddress = {
          _id: "",
          label: place.formatted_address,
          addressLine1: street,
          addressLine2: "",
          country: countryObj.long_name,
          county: cityObj.long_name,
          city: cityObj.long_name,
          state: stateObj.long_name,
          zip: postalCode,
          cityCode: cityObj.short_name,
          stateCode: stateObj.short_name,
          countryCode: countryObj.short_name,
          latitude: lat,
          longitude: lng,
          phone: "",
        };

        setInstallationAddress(newAddress);
        formik.setFieldValue("installationAddressId", place.formatted_address);
      }
    } catch (error) {
      console.error("Geocoder failed", error);
      showError("Unable to update address from marker position.");
    }
  };

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      const membershipId = resolveInstallationPaymentMethodsMembershipId(installationOrder);
      const sellerOrgId = resolveInstallationSellerOrganizationId(
        installationOrder?.signage_id,
        installationOrder
      );
      if (!membershipId || !sellerOrgId || !token) {
        console.warn("fetchPaymentMethods: Missing membershipId or sellerOrgId", {
          membershipId,
          sellerOrgId,
        });
        return;
      }
      try {
        const data = await fetchInstallationPaymentMethods(
          token,
          sellerOrgId,
          membershipId
        );
        console.log("PaymentMethod :", data);
        setPaymentMethod(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching payment methods:", err);
      } finally {
        setLoading(false);
      }
    };

    if (userId && installationOrder?.inventory_source) {
      fetchPaymentMethods();
    }
  }, [token, userId, installationOrder?.inventory_source, installationOrder?.agent_id, installationOrder?.team_member_id, installationOrder?.buyer_membership_id]);

  // setting defult selectedProducts
  useEffect(() => {
    if (installationOrder?.line_items?.length > 0) {
      const mapped = installationOrder.line_items.map((item: any) => ({
        product_id: item.product_id,
        inventory_type: item.inventory_source || "",
        name: item.product_name || "",
        sku: item.sku || "",
        product_location_status: item.current_status || "",
        user_id: "", // You can enrich this if needed based on agent/office ID from elsewhere
        quantity: item.quantity,
        updateStatus: "",
        productionStatus: item.production_status || "",
        is_rotational: item.is_rotational,
        allocatedCount: item.rotational_allocated || 0,
      }));

      setSelectedProducts(mapped);
      console.log("Mapped Products:", mapped);
    }
  }, [installationOrder]);

  // const [integrations, setIntegrations] = useState<PaymentGateway[]>([]);

  // useEffect(() => {
  //   const fetchIntegrations = async () => {
  //     try {
  //       const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/gateway/created-by/${installationOrder?.signage_id}`, {
  //         method: "GET",
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           "Content-Type": "application/json",
  //         },
  //       });
  //       if (!response.ok) {
  //         throw new Error("Failed to fetch integrations.");
  //       }
  //       const data = await response.json();
  //       console.log('Methods ', data);
  //       setIntegrations(data);
  //     } catch (err) {
  //       //showError("Failed to load integrations.");
  //       setError(err instanceof Error ? err.message : "An unknown error occurred");
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchIntegrations();
  // }, [installationOrder]);

  // const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);


  // useEffect(() => {
  //   const stripeInfo = integrations?.find((method) => method.gateway === "stripe");
  //   console.log('Stripe info ', stripeInfo);
  //   if (stripeInfo && stripeInfo.publishable_key) {
  //     const promise = loadStripe(stripeInfo.publishable_key);
  //     setStripePromise(promise);
  //   }
  // }, [integrations]);

  // setting defult billing Address
  useEffect(() => {
    if (!installationOrder) return;

    const billingId =
      installationOrder?.billing_address?._id || formik.values.billingAddressId;
    if (!billingId) return;

    const billingMatch =
      addressObjects.find(
        (addr) =>
          addr._id === billingId || addr.address_details?._id === billingId
      ) || installationOrder?.billing_address;

    const agentBillingDetails = billingMatch?.address_details || billingMatch || null;

    if (agentBillingDetails && (!selectedBillingAddress || selectedBillingAddress._id !== agentBillingDetails._id)) {
      setSelectedBillingAddress(agentBillingDetails);
      formik.setFieldValue("billingAddressId", billingId);
    }
  }, [installationOrder, addressObjects, formik.values.billingAddressId]);


  const completeOrder = async (getmethod: string) => {
    console.log("getmethod", getmethod);
    setLoading(true);

    try {
      if (["cheque", "bank_transfer"].includes(getmethod)) {
        // 🔁 Call Net30-style API
        const latestPayment = installationOrder?.payment_info?.[0]?.amount_due;
        console.log("latestPayment", latestPayment);
        const lastAmountDue = latestPayment ?? formik.values.total;
        console.log("lastAmountDue", lastAmountDue);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/installation_orders/${installationOrder?._id}/net30_payment`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`, // use token from context
            },
            body: JSON.stringify({
              payment_id: formik.values.transactionId || "",
              method: getmethod,
              amount_paid: formik.values.partial_amount,
              total_amount: lastAmountDue,
              amount_due: Math.max(0, lastAmountDue - formik.values.partial_amount),
              payment_date: new Date().toISOString(),
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Cheque/Bank payment failed.");
        }

      } else {
        // 🔁 Default online payment API
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/installation_orders/${installationOrder?._id}/payment`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              payment_id: "",
              total_amount: formik.values.total,
              amount_paid: 0,
              amount_due: formik.values.total,
              payment_date: new Date().toISOString(),
              method: getmethod,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Online payment failed.");
        }
      }
      showSuccess("Payment submitted successfully!");

      navigate("/agent/jobs/installations");
      localStorage.removeItem("cart_id");

    } catch (error) {
      const errMessage =
        (error as Error).message || "An unknown error occurred";
      console.error("Order Failed:", errMessage);
      showError(`Error: ${errMessage}`);
    } finally {
      setLoading(false);
    }
  };


  const handleInstallationStatus = async (nextStatus: number) => {
    if (!installationId || !token) return;

    setLoading(true);

    try {
      let url = "";
      let body: any = undefined;

      const scheduledDate = installationOrder?.installation_details?.scheduled_date;

      switch (nextStatus) {
        case 200: // Finalize
          url = `${process.env.REACT_APP_API_URL}/installation_orders/${installationId}/finalize`;
          break;

        case 600:
          url = `${process.env.REACT_APP_API_URL}/installation_orders/${installationId}/cancel`;
          body = { note: formik.values.instructions };
          break;

        default:
          showError("Invalid status selected");
          setLoading(false);
          return;
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        ...(body ? { body: JSON.stringify(body) } : {}), // only include body if defined
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result?.error || "Status update failed");
      showSuccess(result.message || "Installation status updated");

      setInstallationOrder((prev: any) => ({ ...prev, status: nextStatus }));
    } catch (err: any) {
      console.error("Status update error:", err.message);
      showError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const totalPaid = (installationOrder?.payment_info ?? []).reduce(
    (acc: number, payment: { amount_paid?: number | string }) =>
      acc + Number(payment.amount_paid || 0),
    0
  );

  const maxAllowed = (installationOrder?.payment_info ?? []).length > 0 ? Number((Number(installationOrder?.total_amount || 0) - totalPaid).toFixed(2)) : Number(installationOrder?.total_amount || 0);


  const remaingamount = ((installationOrder?.total_amount || 0) - totalPaid)

  const statusHistory: any[] = Array.isArray(installationOrder?.status_history)
    ? (installationOrder?.status_history as any[])
    : [];
  // console.log("statusHistory", statusHistory);
  const paymentInfo: PaymentInfoItem[] = [...(installationOrder?.payment_info || [])];

  const resolveOrderStatus = (status: number | undefined): string => {
    switch (status) {
      case 100: return "Draft";
      case 200: return "Finalized";
      case 300: return "Scheduled";
      case 400: return "In Progress";
      case 500: return "Completed";
      case 600: return "Canceled";
      default: return status !== undefined ? `Status ${status}` : "Unknown";
    }
  };


  const parseDate = (input: any): string => {
    try {
      return new Date(input).toLocaleString();
    } catch {
      return "Invalid Date";
    }
  };

  useEffect(() => {
    if (installationOrder?.uploaded_files) {
      setUploadedFileMeta(installationOrder.uploaded_files);
    }
  }, [installationOrder]);

  useEffect(() => {
    if (installationOrder?.signage_id && suppliers.length > 0) {
      fetchLinkedProducts(installationOrder.signage_id);
      const defaultSupplier = suppliers.find(
        (sup) => sup.supplier_id === installationOrder.signage_id
      );

      if (defaultSupplier) {
        const option = {
          value: defaultSupplier.supplier_id,
          label: defaultSupplier.name,
        };
        setSelectedInstaller(option);
        formik.setFieldValue("installer", option.value);
        console.log("✅ Default Installer set:", option.value);
      }
    }
  }, [installationOrder, suppliers]);


  useEffect(() => {
    if (installationOrder) {
      const addr = installationOrder?.installation_details?.installation_address;
      setInstallationAddress(addr);
    }
  }, [installationOrder]);

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <div className="content">
      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between gap-2 align-items-center border-bottom pb-3 page-container mb-2">
            {/* <h4>Order Status : <InstallationStatusBadge value={installationOrder?.status ?? ""} type="order" /></h4> */}
            <h4 className="table-headers">Installation Request - Edit</h4>

            <div className="d-flex align-items-center gap-3 flex-wrap">
              {/* {
                                                              removalOrder?.status === 100
                                                                  ? "Draft"
                                                                  : removalOrder?.status === 200
                                                                      ? "Finalized"
                                                                      : removalOrder?.status === 300
                                                                          ? "Scheduled"
                                                                          : removalOrder?.status === 400
                                                                              ? "In Progress"
                                                                              : removalOrder?.status === 500
                                                                                  ? "Completed"
                                                                                  : removalOrder?.status === 600
                                                                                      ? "Cancelled"
                                                                                      : removalOrder?.status === 700
                                                                                          ? "Lost"
                                                                                          : "Unknown"
                                                          } */}
              <div className="dropdown">
                <button
                  className="btn btn-outline-secondary dropdown-toggle btn-sm br-8 table-cells"
                  type="button"
                  id="orderActionsDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Actions
                </button>
                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="orderActionsDropdown">

                  {/* {installationOrder?.status === 100 && (
                                                        <Link to={`/realtor/jobs/editInstallation/${installationOrder._id}`} className="dropdown-item text-primary table-cells">
                                                            <i className="ri-pencil-fill me-2"></i>Click to Finalize
                                                        </Link>
                                                    )} */}
                  {(installationOrder?.status <= 100) && (
                    <button className="dropdown-item text-danger table-cells" onClick={() => handleInstallationStatus(600)}>
                      <i className="mdi mdi-archive-remove-outline me-2"></i>Cancel Order
                    </button>
                  )}
                  {/* {installationOrder?.status === 200 && (
                                            <button className="btn btn-primary" onClick={() => handleInstallationStatus(300)}>
                                                Click to Schedule
                                            </button>
                                        )}
            
                                        {installationOrder?.status === 300 && (
                                            <button className="btn btn-primary" onClick={() => handleInstallationStatus(300)}>
                                                Click to Re-Schedule
                                            </button>
                                        )}
                                        {installationOrder?.status === 300 && (
                                            <button className="btn btn-primary ms-3" onClick={() => handleInstallationStatus(400)}>
                                                Click to In Progress
                                            </button>
                                        )}
            
                                        {installationOrder?.status === 400 && (
                                            <button className="btn btn-primary" onClick={() => handleInstallationStatus(500)}>
                                                Click to Complete
                                            </button>
                                        )} */}
                </ul>
              </div>
              <div>
              </div>
            </div>
          </div>
          <form onSubmit={formik.handleSubmit}>
            <div className="row">
              <div className="col-md-5 mb-3">
                <label className="form-label table-cells">
                  Select Signage company/Installer <span className="text-danger">*</span>
                </label>
                <Select
                  // className="form-control"
                  id="installer"
                  name="installer"
                  options={installerOptions}
                  value={installerOptions.find((option) => option.value === formik.values.installer)}
                  onChange={(selected) => {
                    formik.setFieldValue("installer", selected?.value || "");
                    fetchLinkedProducts(selected?.value || "");
                    setSelectedInstaller(selected);

                    const supplierId = selected?.value ?? "";
                  }}
                  onBlur={formik.handleBlur}
                  isDisabled
                />
                {/* {formik.touched.installer && formik.errors.installer && (
                                      <div className="text-danger">{formik.errors.installer}</div>
                                  )} */}
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label  table-cells">
                  Select Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className={`form-control ${formik.touched.scheduled_date &&
                    formik.errors.scheduled_date
                    ? "is-invalid"
                    : ""
                    }`}
                  id="scheduled_date"
                  name="scheduled_date"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.scheduled_date}
                  disabled={installationOrder?.status > 100}
                />
                {formik.touched.scheduled_date && formik.errors.scheduled_date && (
                  <div className="invalid-feedback">
                    {formik.errors.scheduled_date as string}
                  </div>
                )}

              </div>
            </div>
            <div className="dashed-line my-2"></div>
            <div className="row">
              <h5 className="table-headers">Address Details </h5>
              <div className="my-2 table-cells">
                <input
                  type="checkbox"
                  id="isSameAsBilling"
                  name="isSameAsBilling"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  checked={formik.values.isSameAsBilling}
                />{" "}
                Billing and Installation Address are Different

              </div>
              <div className="row">
                <div className="col-md-3 ms-2" id="row-overlap">
                  <div className="">
                    <label
                      htmlFor="billingAddressId"
                      className="form-label table-cells"
                    >
                      Billing Address <span className="text-danger">*</span>
                    </label>
                    <Select
                      // className="form-control"
                      id="billingAddress"
                      name="billingAddress"
                      options={addressOptions}
                      value={
                        addressOptions?.find(
                          (option) =>
                            option.value === formik.values.billingAddressId
                        ) || null
                      }
                      onChange={(selected) => {
                        console.log("Selected Billing Address:", selected); // Debugging log
                        formik.setFieldValue("billingAddressId", selected?.value); // Ensure it's set
                        const selectedAddress = addressObjects?.find(
                          (addr) => (addr.address_details ? addr.address_details._id : addr._id) === selected?.value
                        );
                        setSelectedBillingAddress(selectedAddress.address_details || null);
                      }}
                      isDisabled={installationOrder?.status > 100}
                    />
                    {formik.touched.billingAddressId &&
                      typeof formik.errors.billingAddressId === "string" && (
                        <div className="text-danger mt-1">
                          {formik.errors.billingAddressId}
                        </div>
                      )}

                  </div>
                </div>

                {formik.values.isSameAsBilling === true ? null : (
                  <div className="col-md-3" id="row-overlap">
                    <div className="">
                      <label
                        htmlFor="installationAddressId"
                        className="form-label table-cells"
                      >
                        Installation Address <span className="text-danger">*</span>
                      </label>
                      {isLoaded && (
                        <Autocomplete
                          onLoad={(autocomplete) => {
                            autocompleteRef.current = autocomplete;
                          }}
                          onPlaceChanged={handlePlaceChanged}
                        >
                          <input
                            type="text"
                            className="form-control"
                            id="installationAddressId"
                            name="installationAddressId"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.installationAddressId}
                            disabled={installationOrder?.status > 100}
                          />
                        </Autocomplete>
                      )}
                      {formik.touched.installationAddressId &&
                        typeof formik.errors.installationAddressId === "string" && (
                          <div className="text-danger mt-1">
                            {formik.errors.installationAddressId}
                          </div>
                        )}

                    </div>
                  </div>
                )}
                {installationOrder?.status === 100 &&
                  <div className="col-md-2" id="row-overlap">
                    <p>&nbsp;</p>
                    <i
                      className="ri-search-line fs-3 text-bg-info"
                      style={{ cursor: "pointer" }}
                      title={`${isMarkerDraggable ? "Disable Marker Drag" : "Enable Marker Drag"}`}
                      onClick={() => {
                        const newValue = !isMarkerDraggable;
                        setIsMarkerDraggable(newValue);
                        showError(
                          newValue
                            ? "You can now move the marker on the map."
                            : "Marker dragging disabled."
                        );
                      }}

                    ></i>
                  </div>
                }
              </div>
              <div className="dashed-line my-2"></div>
              <div className="row ms-2 mb-2" id="row-overlap">
                <h5 className="table-headers text-center">Map View</h5>
                <div style={mapContainerStyle}>
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    zoom={10}
                    center={selectedLocation || center}
                  // onClick={handleMapClick}
                  >
                    {selectedLocation && <Marker
                      position={selectedLocation}
                      draggable={isMarkerDraggable}
                      onDragEnd={handleMarkerDragEnd}
                    />
                    }
                  </GoogleMap>
                </div>
                {formik.touched.location && formik.errors.location && (
                  <div className="text-danger">{formik.errors.location}</div>
                )}
              </div>

              <div className="col-md-12">
                <h4 className="ms-1">Product Selection</h4>
                <div className="row mb-3">
                  <div className="col-md-4 ms-3" id="row-overlap">
                    <Select
                      id="products"
                      name="products"
                      options={productOptions}
                      value={selectedProductOption}
                      onChange={(selected) => {
                        setSelectedProductOption(selected)
                      }}
                      onBlur={formik.handleBlur}
                      isDisabled={installationOrder?.status > 100}
                    />
                  </div>
                  <div className="col-md-3" id="row-overlap">
                    <button
                      className="btn btn-success"
                      type="button"
                      onClick={() => {
                        if (!selectedProductOption) return;

                        const alreadyExists = selectedProducts.some(
                          (prod) => prod.product_id === selectedProductOption.value
                        );
                        if (alreadyExists) {
                          showError("Product already added");
                          return;
                        }

                        const fullProduct = productObjs.find(
                          (p) => p.product_id === selectedProductOption.value
                        );

                        if (fullProduct) {
                          const flattenedProduct = {
                            product_id: fullProduct.product_id,
                            name: fullProduct.product.name,
                            sku: fullProduct.product.sku || "",
                            inventory_type: fullProduct.inventory_type,
                            product_location_status: fullProduct.product.product_location_status,
                            user_id: fullProduct.agent_id || fullProduct.office_id,
                            quantity: 1,
                            updateStatus: "",
                            productionStatus: "",
                          };

                          setSelectedProducts((prev) => [...prev, flattenedProduct]);
                          setSelectedProductOption(null);
                        }
                        console.log("selectedProducts", selectedProducts);
                      }}
                      disabled={installationOrder?.status > 100}
                    >
                      Add Product
                    </button>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12">
                    <table className="table table-bordered table-striped ">
                      <thead className="table-light">
                        <tr>
                          <th className="text-center table-headers" style={{ width: "20%" }}>Name</th>
                          <th className="text-center table-headers" style={{ width: "8%" }}>SKU</th>
                          <th className="text-center table-headers" style={{ width: "8%" }}>Qty </th>
                          <th className="text-center table-headers" style={{ width: "10%" }}>Source </th>
                          <th className="text-center table-headers" style={{ width: "15%" }}>Product Location Status</th>
                          <th className="text-center table-headers" style={{ width: "15%" }}>Production Status </th>
                          <th className="text-center table-headers" style={{ width: "7%" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          selectedProducts?.filter((p) => p.inventory_type !== "rotational" && p.inventory_type !== "").map((item, index) => {
                            // const allocated = allocatedUnits[item.product_id] || [];
                            // const remaining = item.quantity - allocated.length;
                            return (
                              <tr key={item.product_id}>
                                <td className="text-center table-cells">{item.name}</td>
                                <td className="text-center table-cells">{item.sku}</td>
                                <td className="text-center table-cells">
                                  <input
                                    type="number"
                                    className="form-control text-center table-cells"
                                    value={item.quantity}
                                    min={1}
                                    onChange={(e) => {
                                      const qty = Number(e.target.value);
                                      const updated = [...selectedProducts];
                                      updated[index].quantity = qty;
                                      setSelectedProducts(updated);
                                    }}
                                  />

                                </td>
                                <td className="text-center table-cells">
                                  {item.inventory_type === "agent" ? "Agent Inventory" : "Office Inventory"}
                                </td>

                                <td className="text-center table-cells">
                                  <div className="d-flex justify-content-evenly">
                                    <span className=" px-3 py-2" >
                                      <StatusBadge status={item.product_location_status?.replace(/_/g, " ") || "N/A"} />
                                    </span>
                                    {/* <button className="btn btn small btn-primary" onClick={() =>
                                                                            setOpenStatusIndex(openStatusIndex === index ? null : index)
                                                                          }>Update Status</button> */}
                                    <Link
                                      className="border-0 p-0"
                                      to={
                                        item.inventory_type === "agent"
                                          ? "/signage/incomingAgentProducts"
                                          : "/signage/incomingOfficeProducts"
                                      }
                                    >
                                      <i className="ri-link-m fs-24"></i>
                                    </Link>

                                  </div>
                                  {/* {openStatusIndex === index && (
                                                                          <>
                                                                            <div className="mt-2">
                                                                              <Select
                                                                                id="update_status"
                                                                                name="update_status"
                                                                                options={[
                                                                                  { value: "with supplier", label: "With Supplier" },
                                                                                  { value: "in transit", label: "In Transit" },
                                                                                  { value: "on site", label: "On Site" },
                                                                                  { value: "declined", label: "Declined" },
                                                                                ]}
                                                                                onChange={(option) =>
                                                                                  formik.setFieldValue("update_status", option?.value || "")
                                                                                }
                                                                                onBlur={formik.handleBlur}
                                                                              />
                                                                            </div>
                                                                            <div className="mt-2">
                                                                              <button className="btn btn-info small w-100">Update</button>
                                                                            </div>
                                                                          </>
                                                                        )} */}
                                </td>
                                <td className="text-center table-cells">
                                  {/* <Select
                                                                          id="product_status"
                                                                          name="product_status"
                                                                          options={[
                                                                            { value: "not started", label: "Not Started" },
                                                                            { value: "in production", label: "In Production" },
                                                                            { value: "quality check", label: "Quality Check" },
                                                                            { value: "ready for installation", label: "Ready for Installation" },
                                                                          ]}
                                                                          onChange={(option) =>
                                                                            formik.setFieldValue("company", option?.value || "")
                                                                          }
                                                                          onBlur={formik.handleBlur}
                                                                        /> */}
                                  ---
                                </td>
                                <td className="text-center table-cells d-flex gap-2 justify-content-center">
                                  {/* <button
                                                          type="button"
                                                          className="btn btn-success btn-sm"
                                                          disabled={remaining <= 0}
                                                          onClick={() => {
                                                            setLinkedProductIdContext(item.product_id);
                                                            setSelectedProduct(null);
                                                            setSelectedWarehouse(null);
                                                            setAvailableSerializedUnits([]);
                                                            fetchRotationalPoolProducts();
                                                            setShowAllocateModal(true);
                                                          }}
                                                        >
                                                          Allocate
                                                        </button> */}

                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    onClick={() => {
                                      setSelectedProducts((prev) =>
                                        prev.filter((prod) => prod.product_id !== item.product_id)
                                      );
                                      // setAllocatedUnits((prev) => {
                                      //   const updated = { ...prev };
                                      //   delete updated[item.product_id];
                                      //   return updated;
                                      // });
                                    }}
                                    disabled={installationOrder?.status > 100}
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            )
                          })

                        }

                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-12 my-3 text-end">
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-7">
                  <label htmlFor="" className="table-headers">Instructions </label>
                  <textarea
                    name="instructions"
                    id="instructions"
                    className="form-control"
                    placeholder="Add instructions here"
                    value={formik.values.instructions}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    rows={4}
                  ></textarea>
                  <div className="col-md-4 mt-5 table-cells">
                    <label className="me-1 mb-1 table-headers" >Local Laws Compliance :</label><br />
                    <input
                      type="checkbox"
                      id="localLaws"
                      name="localLawsChecked"
                      checked={formik.values.localLawsChecked}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <span>{" "}I acknowledge adherence to local laws</span>

                  </div>

                  {/* <div className="col-md-12 mt-3">
                    <label htmlFor="">Proof of Installation</label>
                    <input type="file" className="form-control" id="image" name="image" multiple onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      const newPreviewFiles = files.map((file) => ({
                        file,
                        url: URL.createObjectURL(file),
                      }));
                      setPreviewFiles((prev) => [...prev, ...newPreviewFiles]);
                    }} />
                    <button
                      type="button"
                      className="btn btn-success mt-3"
                      onClick={async () => {
                        try {
                          const uploads = await Promise.all(
                            previewFiles.map(async ({ file }) => {
                              const path = await uploadFile(file);
                              return {
                                file_name: file.name,
                                file_url: path,
                                content_type: file.type,
                                uploaded_date: new Date().toISOString(),
                              };
                            })
                          );

                          const allFiles = [...uploadedFileMeta, ...uploads];
                          const uniqueByName = allFiles.filter(
                            (file, index, self) =>
                              index === self.findIndex((f) => f.file_name === file.file_name)
                          );

                          setUploadedFileMeta(uniqueByName);
                          showSuccess("Files uploaded successfully!");
                          setPreviewFiles([]); // clear previews after upload
                        } catch (err) {
                          console.error("Upload error:", err);
                         showError("Failed to upload files.");
                        }
                      }}

                    >
                      Upload
                    </button>

                  </div> */}
                </div>
                <div className="col-md-3 offset-2">
                  <table className="calculation-table mt-3">
                    <tr>
                      <th className="table-headers"> Total Units</th>
                      <td className="table-cells">{totalUnits}</td>
                    </tr>
                    <tr>
                      <th className="table-headers"> Installation Charge</th>
                      <td className="table-cells">$ {Number(installationCost)?.toFixed(2) ?? "0.00"}</td>
                    </tr>
                    <tr>
                      <th className="table-headers"> Tax</th>
                      <td className="table-cells">$ {Number(formik.values.tax).toFixed(2)}</td>
                    </tr>
                    <tr>
                      <th className="table-headers"> Subtotal</th>
                      <td className="table-cells">$ {Number(formik.values.subtotal).toFixed(2)}</td>
                    </tr>
                    <tr>
                      <th className="table-headers"> Total</th>
                      <td className="table-cells">$ {Number(formik.values.total).toFixed(2)}</td>
                    </tr>
                  </table>

                </div>
              </div>
            </div>

            <div className="row mt-3">
              {uploadedFileMeta?.map((file, index) => (
                <div key={index} className="col-md-3 position-relative mb-3">
                  {
                    installationOrder?.status === 100 &&
                    <div
                      className="position-absolute top-0 end-0 bg-danger text-white rounded-circle"
                      style={{
                        width: "20px",
                        height: "20px",
                        textAlign: "center",
                        cursor: "pointer",
                        zIndex: 2,
                      }}
                      onClick={() => {
                        setUploadedFileMeta((prev) => prev.filter((_, i) => i !== index));
                      }}
                    >
                      &times;
                    </div>
                  }
                  <div className="border p-2">
                    <img
                      src={`${process.env.REACT_APP_API_URL}/${file.file_url.replace(/\\/g, "/")}`}
                      alt={file.file_name}
                      className="img-fluid"
                      style={{ height: "150px", objectFit: "cover", width: "100%" }}
                    />
                    <div className="small text-center mt-1 text-muted">{file.file_name}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="dashed-line my-2"></div>
            <div className="row mb-3 rcust-margin-4">
              <div className="col-md-12 mt-2 d-flex justify-content-between align-items-center">
                <button
                  className="btn btn-outline-secondary br-8 backbutton"
                  type="button"
                  onClick={() => navigate(-1)}
                >
                  Back
                </button>

                <button
                  type="submit"
                  className="btn btn-info"
                  disabled={formSubmitting || !formik.values.localLawsChecked}
                >
                  {formSubmitting ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </div>

            <div className="card mt-4">
              <div className="card-header">
                <h5 className="card-title mb-0 table-headers">Installation Log</h5>
              </div>
              <div className="card-body">
                <ul className="list-unstyled vstack gap-3">

                  {/* ✅ Payment Logs */}
                  {paymentInfo.slice().reverse()?.map((payment, idx) => {
                    const status =
                      payment.amount_due === 0
                        ? "Paid"
                        : payment.amount_paid > 0
                          ? "Partial Payment"
                          : "Pending";

                    const badgeClass =
                      status === "Paid"
                        ? "bg-success-subtle text-success"
                        : status === "Partial Payment"
                          ? "bg-warning-subtle text-warning"
                          : "bg-danger-subtle text-danger";

                    return (
                      <li key={`payment-${idx}`}>
                        <div>
                          <strong className="table-cells">💰 Payment:</strong>{" "}
                          <span className="badge bg-success-subtle text-success table-cells">
                            ${payment.amount_paid?.toFixed(2)} {payment.method && `via ${payment.method}`}
                          </span>

                          <strong className="ms-3 table-cells">💳 Payment Status:</strong>{" "}
                          <span className={`badge ${badgeClass}`}>{status}</span>

                          <div className="text-muted small table-cells">On {parseDate(payment.payment_date)}</div>
                        </div>
                      </li>
                    );
                  })}

                  {/* ✅ Installation Status Logs */}
                  {statusHistory.map((log, idx) => (
                    <li key={`status-${idx}`}>
                      <div>
                        <strong className="table-cells">📦 Installation Status:</strong>{" "}
                        <span className="badge bg-secondary-subtle text-dark">
                          {
                            log.from === null && resolveOrderStatus(log.from)
                          }
                          {resolveOrderStatus(log.from)} → {resolveOrderStatus(log.to)}
                        </span>
                        <div className="text-muted small table-cells">On {parseDate(log.timestamp)}</div>
                        {log.note && <div className="small mt-1">📝 {log.reason}</div>}
                      </div>
                    </li>
                  ))}

                </ul>
              </div>
            </div>

          </form>

          {/* <div className="row mt-3">
            <div className="col-md-9">
              <h3>Payment Methods</h3>
              {
                installationOrder?.status === 100 && <h5 className="text-danger">To Get the Payment Method Finalize the Installation Order</h5>
              }
              {((installationOrder?.status ?? 0) > 100) && installationOrder?.payment_status !== "Paid" && (
                <>
                  <button
                    type="button"
                    className="btn btn-success material-shadow-none btn-sm mt-2 mt-sm-0 me-1"
                    onClick={() => setPayment((prev) => !prev)}
                  >
                    <i className="mdi mdi-cash-multiple align-middle me-1"></i>
                    Process Payment
                  </button>
                </>
              )}
              {payment && installationOrder?._id && (
                <div className="card-body">
                  <div className="row my-3">
                    <div className="col-md-6">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="enable_partial_payment"
                          name="enable_partial_payment"
                          onChange={(e) => {
                            formik.handleChange(e);
                            if (!e.target.checked) {
                              formik.setFieldValue("partial_amount", 0);
                            }
                          }}
                          onBlur={formik.handleBlur}
                          checked={formik.values.enable_partial_payment}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="enable_partial_payment"
                        >
                          Enable Partial Payment
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      {formik.values.enable_partial_payment && (
                        <>
                          <div className="mt-2">
                            <label className="form-label">
                              Amount to Collect Now :
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              name="partial_amount"
                              id="partial_amount"
                              onChange={(e) => {
                                const enteredValue = Number(e.target.value);
                                formik.setFieldValue(
                                  "partial_amount",
                                  enteredValue <= maxAllowed ? enteredValue : maxAllowed
                                );
                              }}
                              onBlur={formik.handleBlur}
                              value={formik.values.partial_amount}
                            />

                          </div>
                          <div className="mt-2">
                            <span>
                              Remaining Balance: $
                              {(Number(remaingamount) - Number(formik.values.partial_amount || 0)).toFixed(2)}

                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <Elements stripe={stripePromise}>
                        <div>
                          <h5 className="mb-1">Payment Selection</h5>
                          <p className="text-muted mb-2">
                            Please select payment method
                          </p>
                        </div>
                        <div className="row">
                          <div className="col-lg-3 mb-3 col-sm-6">
                            <div className="form-check card-radio">
                              <input
                                id="paymentMethodOnline"
                                name="paymentMethod"
                                type="radio"
                                className="form-check-input"
                                checked={selectedPaymentMethod === "online"}
                                onChange={() =>
                                  setSelectedPaymentMethod("online")
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor="paymentMethodOnline"
                              >
                                <i className="ri-bank-card-fill align-bottom"></i>
                                <span> Online Payment</span>
                              </label>
                            </div>
                          </div>
                          <div className="col-lg-3 mb-3 col-sm-6">
                            <div className="form-check card-radio">
                              <input
                                id="paymentMethodcheque"
                                name="paymentMethod"
                                type="radio"
                                className="form-check-input"
                                checked={selectedPaymentMethod === "cheque"}
                                onChange={() =>
                                  setSelectedPaymentMethod("cheque")
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor="paymentMethodcheque"
                              >
                                <i className="ri-bank-card-fill align-bottom"></i>
                                <span> Cheque</span>
                              </label>
                            </div>
                          </div>
                          <div className="col-lg-3 mb-3 col-sm-6">
                            <div className="form-check card-radio">
                              <input
                                id="paymentMethodbank_transfer"
                                name="paymentMethod"
                                type="radio"
                                className="form-check-input"
                                checked={selectedPaymentMethod === "bank_transfer"}
                                onChange={() =>
                                  setSelectedPaymentMethod("bank_transfer")
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor="paymentMethodbank_transfer"
                              >
                                <i className="ri-bank-card-fill align-bottom"></i>
                                <span> Bank Transfer</span>
                              </label>
                            </div>
                          </div>
                          {(formik.values.enable_partial_payment || installationOrder?.payment_info?.length) ? <></> :
                            (
                              paymentMethod?.map((method) => (
                                <div className="col-lg-3 mb-3 col-sm-6" key={method.id}>
                                  <div className="form-check card-radio">
                                    <input
                                      id={`paymentMethod_${method.id}`}
                                      name="paymentMethod"
                                      type="radio"
                                      className="form-check-input"
                                      checked={
                                        selectedPaymentMethod === method.name
                                      }
                                      onChange={() =>
                                        setSelectedPaymentMethod(method.name)
                                      }
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor={`paymentMethod_${method.id}`}
                                    >
                                      <i className="ri-bank-card-fill align-bottom"></i>
                                      <span> {method.name}</span>
                                    </label>
                                  </div>
                                </div>
                              ))
                            )
                          }
                        </div>

                        {selectedPaymentMethod === "online" ? (
                          <div className="collapse show mt-4">
                            <div className="card p-4 border shadow-none">
                              <PaymentFormManualOrder
                                url="installation_orders"
                                method="POST"
                                supplierId={installationOrder?.signage_id}
                                totalAmount={remaingamount ? remaingamount : installationOrder?.total_amount}
                                paidAmount={
                                  formik.values.partial_amount === 0
                                    ? (Number(installationOrder?.total_amount || 0) - totalPaid)
                                    : formik.values.partial_amount
                                }
                                orderId={installationOrder?._id}
                                selectedPaymentMethod={selectedPaymentMethod}
                                onPaymentSuccess={(paymentIntentId) => {
                                  console.log(
                                    "Payment Success: ",
                                    paymentIntentId
                                  );
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          ["cheque", "bank_transfer"].includes(selectedPaymentMethod) ? (
                            <div className="mt-4">
                              <div className="card p-3 border shadow-none">
                                <h4 className="mb-3">
                                  {selectedPaymentMethod === "cheque" ? "Cheque Payment Details" : "Bank Transfer Details"}
                                </h4>
                                <div className="row">
                                  <div className="mb-3 col-md-6">
                                    <label htmlFor="transactionId" className="form-label">
                                      Transaction ID
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      id="transactionId"
                                      value={formik.values.transactionId}
                                      onChange={(e) => formik.setFieldValue("transactionId", e.target.value)}
                                    />
                                  </div>

                                  <div className="mb-3 col-md-6">
                                    <label htmlFor="amount" className="form-label">
                                      Amount
                                    </label>
                                    <input
                                      type="number"
                                      className="form-control"
                                      id="amount"
                                      value={formik.values.partial_amount}
                                      onChange={(e) =>
                                        formik.setFieldValue(
                                          "partial_amount",
                                          Number(e.target.value) <= maxAllowed ? Number(e.target.value) : maxAllowed
                                        )
                                      }
                                    />
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  onClick={() => {
                                    completeOrder(selectedPaymentMethod);
                                  }}
                                >
                                  Submit Payment
                                </button>
                              </div>
                            </div>
                          ) : (
                            selectedPaymentMethod ? (
                              <div className="d-flex align-items-start gap-3 mt-4">
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  onClick={() => {
                                    completeOrder(selectedPaymentMethod);
                                  }}
                                >
                                  Complete Order
                                </button>
                              </div>
                            ) : null
                          )
                        )}
                      </Elements>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default AgentEditInstallation;
